import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Tier, Grab } from '../types';
import tierService from '../services/tierService';
import grabService from '../services/grabService';
import { Card, Badge, LoadingScreen, Icon, Button, Field, Input, DateInput, Select, FormActions } from '../components/ui';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { ZurueckLink } from '../components/ZurueckLink';

// ISO (yyyy-mm-dd) -> dd.mm.yyyy, ohne Zeitzonen-Verschiebung.
const fmtDatum = (iso?: string): string => {
  if (!iso) return '–';
  const [y, m, d] = iso.split('T')[0].split('-');
  return (y && m && d) ? `${d}.${m}.${y}` : iso;
};
const isoDay = (iso?: string) => (iso ? iso.split('T')[0] : '');

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-gray-100 py-2.5 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-right text-sm font-medium text-gray-900">{value ?? '–'}</span>
  </div>
);

/**
 * Tier-Detailseite (/tiere/:id; Zeilenklick auf der Tiere-Liste): zeigt die Tierdaten und die
 * Verknüpfungen zu Grab/Kunde/Vertrag. Tier wird inline bearbeitet oder gelöscht.
 */
const TierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tierId = id ? parseInt(id) : NaN;

  const [tier, setTier] = useState<Tier | null>(null);
  const [graeber, setGraeber] = useState<Grab[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({});

  const load = useCallback(async () => {
    if (!tierId || Number.isNaN(tierId)) { setLoading(false); return; }
    try {
      const [t, gs] = await Promise.all([tierService.getById(tierId), grabService.getAll()]);
      setTier(t); setGraeber(gs);
    } catch { toast.error('Tier konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [tierId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!tier) return;
    setForm({
      grabId: tier.grabId, name: tier.name || '', art: tier.art || '', rasse: tier.rasse || '',
      gebDatum: isoDay(tier.gebDatum), sterbeDatum: isoDay(tier.sterbeDatum),
      gewicht: tier.gewicht ?? '', groesse: tier.groesse || '', beisetzungsdatum: isoDay(tier.beisetzungsdatum),
    });
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setForm({}); };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tier) return;
    if (!form.grabId) { toast.error('Bitte ein Grab wählen'); return; }
    setSaving(true);
    try {
      await tierService.update(tier.id, {
        grabId: form.grabId, name: form.name || undefined, art: form.art || undefined, rasse: form.rasse || undefined,
        gebDatum: form.gebDatum || undefined, sterbeDatum: form.sterbeDatum || undefined,
        gewicht: form.gewicht !== '' && form.gewicht != null ? parseFloat(form.gewicht) : undefined,
        groesse: form.groesse || undefined, beisetzungsdatum: form.beisetzungsdatum || undefined, active: true,
      });
      toast.success('Tier gespeichert'); markiereNeu(`tier-${tier.id}`); cancel(); await load();
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Tier konnte nicht gespeichert werden'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!tier) return;
    if (!window.confirm(`Tier „${tier.name || 'ohne Name'}“ löschen?`)) return;
    try {
      await tierService.delete(tier.id);
      toast.success('Tier gelöscht');
      navigate('/tiere');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Tier konnte nicht gelöscht werden'); }
  };

  if (loading) return <LoadingScreen />;
  if (!tier) return <div className="p-6">Tier nicht gefunden. <Link to="/tiere" className="text-brand-600">Zurück</Link></div>;

  return (
    <div className="mx-auto max-w-3xl p-6">
      <ZurueckLink fallback="/tiere" fallbackLabel="Zurück zu den Tieren" />

      <div className="mb-6 mt-3 flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-gray-900">
          <Icon.Paw className="h-6 w-6 text-gray-400" /> {tier.name || 'Tier'}
        </h1>
        {tier.art && <Badge className="bg-brand-100 text-brand-800">{tier.art}</Badge>}
        {!editing && (
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={startEdit} icon={<Icon.Edit className="h-4 w-4" />}>Bearbeiten</Button>
            <Button size="sm" variant="secondary" onClick={handleDelete} className="!text-red-600 hover:!bg-red-50" icon={<Icon.Trash className="h-4 w-4" />}>Löschen</Button>
          </div>
        )}
      </div>

      <Card ref={ankunft(`tier-${tier.id}`).ref} className={`mb-6 p-6 ${ankunft(`tier-${tier.id}`).className}`}>
        <h2 className="mb-3 text-base font-semibold text-gray-900">Tierdaten</h2>
        {editing ? (
          <form onSubmit={save} className="ui-aufklappen space-y-3">
            <Field label="Grab" required>
              <Select value={form.grabId} onChange={e => setForm({ ...form, grabId: parseInt(e.target.value) })}>
                {graeber.map(g => <option key={g.id} value={g.id}>{g.friedhofName ? `${g.friedhofName} – ` : ''}{g.abteilung}/{g.grabnummer}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Art"><Input value={form.art} onChange={e => setForm({ ...form, art: e.target.value })} placeholder="Hund, Katze, …" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Rasse"><Input value={form.rasse} onChange={e => setForm({ ...form, rasse: e.target.value })} /></Field>
              <Field label="Größe"><Input value={form.groesse} onChange={e => setForm({ ...form, groesse: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Geburtsdatum"><DateInput value={form.gebDatum} onChange={v => setForm({ ...form, gebDatum: v })} /></Field>
              <Field label="Sterbedatum"><DateInput value={form.sterbeDatum} onChange={v => setForm({ ...form, sterbeDatum: v })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gewicht (kg)"><Input type="number" step="0.01" value={form.gewicht} onChange={e => setForm({ ...form, gewicht: e.target.value })} /></Field>
              <Field label="Beisetzung"><DateInput value={form.beisetzungsdatum} onChange={v => setForm({ ...form, beisetzungsdatum: v })} /></Field>
            </div>
            <FormActions>
              <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
              <Button type="submit" loading={saving}>Speichern</Button>
            </FormActions>
          </form>
        ) : (
          <>
            <Row label="Name" value={tier.name} />
            <Row label="Art" value={tier.art} />
            <Row label="Rasse" value={tier.rasse} />
            <Row label="Geburtsdatum" value={tier.gebDatum && fmtDatum(tier.gebDatum)} />
            <Row label="Sterbedatum" value={tier.sterbeDatum && fmtDatum(tier.sterbeDatum)} />
            <Row label="Beisetzung" value={tier.beisetzungsdatum && fmtDatum(tier.beisetzungsdatum)} />
            <Row label="Gewicht" value={tier.gewicht != null ? `${tier.gewicht} kg` : undefined} />
            <Row label="Größe" value={tier.groesse} />
          </>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="mb-3 text-base font-semibold text-gray-900">Verknüpfungen</h2>
        <Row label="Grab" value={tier.grabId
          ? <Link to={`/graeber/${tier.grabId}`} className="text-brand-700 hover:underline">{tier.grabNummer}{tier.friedhofName ? ` – ${tier.friedhofName}` : ''}</Link>
          : undefined} />
        <Row label="Kunde" value={tier.kundeId
          ? <Link to={`/kunden/${tier.kundeId}`} className="text-brand-700 hover:underline">{tier.kundeName}</Link>
          : undefined} />
        <Row label="Vertrag" value={tier.vertragId
          ? <Link to={`/vertraege/${tier.vertragId}`} className="text-brand-700 hover:underline">{tier.vertragsnummer}{tier.vertragStatus === 'BEENDET' ? ' (beendet)' : ''}</Link>
          : undefined} />
      </Card>
    </div>
  );
};

export default TierDetail;
